import _ from "lodash";
import {
  getProfilesChangedList,
  getSavedProfilesMap,
  getSavedProfilesList,
  getVisibleProfiles,
} from "../../../../../store/profiles/profiles.selectors";
import {
  Notificator,
  NotificationActionTypes,
} from "../../../../../components/notification";
import {
  ADD_SS_PROFILE,
  REMOVE_SS_PROFILE,
  SET_SUBSCRIBER_SAFETY,
  GET_SS_PROFILES_SUCCESS,
  GET_SS_PROFILES_FAILURE,
  SAVE_SS_PROFILE_SUCCESS,
  SAVE_SS_PROFILES_SUCCESS,
  ADD_SS_PROFILES,
  AddSsProfileAction,
  RemoveSsProfileAction,
  SetSubscriberSafetyAction,
  GetSsProfilesSuccessAction,
  GetSsProfilesFailureAction,
  SaveSsProfileSuccessAction,
  SBSSProfile,
  SaveSsProfilesSuccessAction,
  AddSsProfilesAction,
  SaveSsProfilesFailureAction,
  SAVE_SS_PROFILES_FAILURE,
} from "./ssProfiles.types";
import { SBThunkAction } from "../../../../../store/redux.types";
import { getSubscriberId } from "../../../../../store/info/info.selectors";
import { getGroupDefaults } from "../../../../../store/config.selectors";
import { isFailure, Service, SSProfile } from "@sportal/api";
import { GroupDefaults } from "../../../../../api/ssm/appConfigService.types";
import {
  ProfileType,
  SBProfile,
} from "../../../../../store/profiles/profiles.types";
import { RootState } from "../../../../../store/root.reducer";
import {
  getSSProfilesChangedList,
  getSSProfilesSavedList,
} from "./ssProfiles.selectors";

const getSSProfileEnabledValue = (
  state: RootState,
  type: ProfileType
): boolean => {
  const defaults = getGroupDefaults(state) || ({} as GroupDefaults);

  return defaults[type] && defaults[type][Service.SubscriberSafety];
};

export const addSsProfileRequest =
  (
    profileId: string
  ): SBThunkAction<
    Promise<void>,
    SaveSsProfilesSuccessAction | SaveSsProfilesFailureAction
  > =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberId = getSubscriberId(state);
    const list = getProfilesChangedList(state);
    const { name, type } = list[profileId];
    const enabled = getSSProfileEnabledValue(state, type);

    const result = await api.ssm.safety.update(subscriberId, name, {
      [Service.SubscriberSafety]: enabled,
    });

    if (isFailure(result)) {
      dispatch(saveSsProfilesFailure(result.error));
      return Promise.reject();
    }

    dispatch(addSsProfiles());
    dispatch(saveSsProfilesSuccess([{ id: profileId, enabled }]));
  };

export const addSsProfiles =
  (): SBThunkAction<void, AddSsProfilesAction> => (dispatch, getState) => {
    const state = getState();
    const list = getVisibleProfiles(state);

    const ssProfiles = _.map(list, ({ id, type }) => ({
      id,
      enabled: getSSProfileEnabledValue(state, type),
    }));

    dispatch({
      type: ADD_SS_PROFILES,
      payload: ssProfiles,
    });
  };

export const addSsProfileWithDefault =
  (profileId: string): SBThunkAction<void, AddSsProfileAction> =>
  (dispatch, getState) => {
    const state = getState();
    const list = getProfilesChangedList(state);
    const { type } = list[profileId];
    const enabled = getSSProfileEnabledValue(state, type);

    dispatch(addSsProfile({ id: profileId, enabled }));
  };

export const getSsProfiles =
  (): SBThunkAction<
    Promise<void>,
    | GetSsProfilesSuccessAction
    | GetSsProfilesFailureAction
    | NotificationActionTypes
  > =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const profileList = getSavedProfilesList(state) as SBProfile[];
    const ssProfiles = getSSProfilesChangedList(state);
    const subscriberId = getSubscriberId(state);

    if (_.isEmpty(ssProfiles)) {
      const result = await api.ssm.safety.get(subscriberId);

      if (isFailure(result)) {
        dispatch(getSsProfilesFailure(result.error));
        dispatch(Notificator.error("failed_to_fetch_ss_profiles"));

        return Promise.reject();
      }

      dispatch(
        getSsProfilesSuccess(
          _.map(result.data, (ssProfile) => {
            const profile = _.find(profileList, {
              name: ssProfile.name,
            });

            return {
              id: profile.id,
              enabled: ssProfile.enabled,
            };
          })
        )
      );
    }

    return Promise.resolve();
  };

export const saveSsProfile =
  (
    profileId: string
  ): SBThunkAction<
    Promise<void>,
    SaveSsProfilesSuccessAction | SaveSsProfilesFailureAction
  > =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberId = getSubscriberId(state);
    const list = getSavedProfilesMap(state);
    const ssList = getSSProfilesChangedList(state);
    const savedSsList = getSSProfilesSavedList(state);

    const { name } = list[profileId];
    const { enabled } = ssList[profileId];

    if (enabled === savedSsList[profileId].enabled) {
      return Promise.resolve();
    }

    const result = await api.ssm.safety.update(subscriberId, name, {
      [Service.SubscriberSafety]: enabled,
    });

    if (isFailure(result)) {
      dispatch(saveSsProfilesFailure(result.error));
      return Promise.reject();
    }

    dispatch(saveSsProfilesSuccess([{ id: profileId, enabled }]));
  };

export const saveSsProfiles =
  (
    profiles: SBProfile[]
  ): SBThunkAction<
    Promise<void>,
    | SaveSsProfilesSuccessAction
    | SaveSsProfilesFailureAction
    | GetSsProfilesFailureAction
    | NotificationActionTypes
  > =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberId = getSubscriberId(state);
    const list = getProfilesChangedList(state);
    const ssList = getSSProfilesChangedList(state);
    const savedSsList = getSSProfilesSavedList(state);

    const ssProfiles: SSProfile[] = _.filter(
      ssList,
      ({ id, enabled }) =>
        _.some(profiles, { id }) &&
        (!savedSsList[id] || savedSsList[id].enabled !== enabled)
    ).map(({ id, enabled }) => ({
      name: list[id].name,
      enabled: !!enabled,
    }));

    let error;
    for (const profile of ssProfiles) {
      const result = await api.ssm.safety.update(subscriberId, profile.name, {
        [Service.SubscriberSafety]: profile.enabled,
      });

      if (isFailure(result)) {
        error = result.error;
      }
    }

    if (error) {
      dispatch({ type: SAVE_SS_PROFILES_FAILURE, payload: error });
      dispatch(Notificator.error("failed_to_save_ss_profiles"));
      return Promise.reject();
    }

    dispatch(saveSsProfilesSuccess(_.values(ssList)));
  };

export const addSsProfile = ({
  id,
  enabled,
}: {
  id: string;
  enabled: boolean;
}): AddSsProfileAction => ({
  type: ADD_SS_PROFILE,
  payload: { id, enabled },
});

export const removeSsProfile = (id: string): RemoveSsProfileAction => ({
  type: REMOVE_SS_PROFILE,
  payload: id,
});

export const setSubscriberSafety = (
  id: string,
  enabled: boolean
): SetSubscriberSafetyAction => ({
  type: SET_SUBSCRIBER_SAFETY,
  payload: { enabled, id },
});

export const getSsProfilesSuccess = (
  ssProfiles: SBSSProfile[] = []
): GetSsProfilesSuccessAction => ({
  type: GET_SS_PROFILES_SUCCESS,
  payload: ssProfiles,
});

export const getSsProfilesFailure = (error: {
  code: string | number;
}): GetSsProfilesFailureAction => ({
  type: GET_SS_PROFILES_FAILURE,
  payload: error,
});

export const saveSsProfileSuccess = (
  ssProfile: SBSSProfile
): SaveSsProfileSuccessAction => ({
  type: SAVE_SS_PROFILE_SUCCESS,
  payload: ssProfile,
});

export const saveSsProfilesSuccess = (
  ssProfiles: SBSSProfile[] = []
): SaveSsProfilesSuccessAction => ({
  type: SAVE_SS_PROFILES_SUCCESS,
  payload: ssProfiles,
});

export const saveSsProfilesFailure = (error: {
  code: string | number;
}): SaveSsProfilesFailureAction => ({
  type: SAVE_SS_PROFILES_FAILURE,
  payload: error,
});
